import * as React from 'react';
import { MapPin } from 'lucide-react';
import { ListTable, type ListColumn } from './list-table.js';
import { dueKind, daysUntilDue, formatDueDate } from '../lib/task-board.js';
import type { Task } from '../lib/tasks-api.js';
import { useT } from '../lib/i18n.js';

/**
 * Krok UXF5 bod 2 (nalez A2 / #33) — PRACOVNI FRONTA UKOLU JAKO SEZNAM.
 *
 * Re-audit GPT (10. 10. 2026, dukazni snimek E05): vychozi kanban o peti
 * sloupcich necte jako fronta — v prvni karte stoji dlouha citace e-mailu
 * a ve ctyrech uzkych sloupcich vedle ni se bez rozkliknuti nedozvi makler
 * ani termin. Seznam ukazuje pro KAZDY ukol presne to, co audit zada:
 * `Úkol / zakázka / priorita / termín / makléř / akce`.
 *
 * Tabulku kresli SPOLECNA `ListTable` (krok FUNK1 / UXK3), takze na 390 px
 * se radky prekresli na karty s popisky ze stejnych retezcu jako hlavicka —
 * zadny druhy tabulkovy system.
 *
 * ROZHODNUTI UXF5-PRIORITA: `GET /v1/crm/tasks` zadne pole `priority`
 * nevraci (viz `docs/api/openapi.json`). Sloupec „Priorita" proto ukazuje
 * NALEHAVOST ODVOZENOU Z TERMINU — a to tou SAMOU funkci `dueKind`, kterou
 * se cerveni termin na karte tabule. Druhy vypocet „jak je to hore" by se
 * s kartou rozesel; vymyslet nove pole by znamenalo slibit funkci, kterou
 * server nema.
 */
export interface TaskListProps {
  readonly items: readonly Task[];
  readonly now: Date;
  readonly onOpen: (id: string) => void;
  readonly caseName: (task: Task) => string | null;
  readonly onOpenCase: (task: Task) => (() => void) | null;
  readonly ownerName: (task: Task) => string | null;
  /** JEDNA kontextova akce — tataz funkce, jakou dostava tabule (`taskAction`). */
  readonly action: (task: Task) => { readonly label: string; readonly onAction: () => void } | null;
  /** Lidsky popis sloupce, ve kterem ukol stoji (SSOT: popisky z API). */
  readonly statusLabel: (task: Task) => string;
  readonly empty?: React.ReactNode;
}

export function TaskList({
  items,
  now,
  onOpen,
  caseName,
  onOpenCase,
  ownerName,
  action,
  statusLabel,
  empty,
}: TaskListProps): React.JSX.Element {
  const t = useT();

  const priorityText = React.useCallback(
    (task: Task): { readonly text: string; readonly urgent: boolean } => {
      switch (dueKind(task.dueAt, now)) {
        case 'overdue':
          return { text: t('webAdmin.tasks.overdue'), urgent: true };
        case 'today':
          return { text: t('webAdmin.tasks.dueToday'), urgent: true };
        /* Krok NOC3 bod 3 (nalez N20): „zítra" ma vlastni vetu (klic
           `dueTomorrow` v katalogu uz byl) a NENI nalehave — cervena u ukolu,
           ktery je splatny az zitra, lhala o terminu. */
        case 'tomorrow':
          return { text: t('webAdmin.tasks.dueTomorrow'), urgent: false };
        case 'later':
          return { text: t('webAdmin.tasks.priorityPlanned'), urgent: false };
        default:
          return { text: t('webAdmin.tasks.noDue'), urgent: false };
      }
    },
    [now, t],
  );

  const columns: readonly ListColumn<Task>[] = [
    {
      key: 'title',
      label: t('webAdmin.tasks.drawer.heading'),
      cell: (task) => (
        <button
          type="button"
          className="rh-a2-link"
          data-testid={`task-row-open-${task.id}`}
          onClick={() => onOpen(task.id)}
        >
          {task.title}
        </button>
      ),
    },
    {
      key: 'case',
      label: t('webAdmin.tasks.drawer.case'),
      cell: (task) => {
        const name = caseName(task);
        if (name === null) return <span className="rh-a2-meta">—</span>;
        const open = onOpenCase(task);
        return (
          <button
            type="button"
            className="rh-a2-tag rh-a2-tag--case"
            data-testid={`task-row-case-${task.id}`}
            disabled={open === null}
            title={open === null ? undefined : t('webAdmin.tasks.openCase')}
            onClick={() => open?.()}
          >
            <MapPin size={11} strokeWidth={2} aria-hidden="true" />
            <span>{name}</span>
          </button>
        );
      },
    },
    {
      key: 'priority',
      label: t('webAdmin.tasks.columnPriority'),
      cell: (task) => {
        const priority = priorityText(task);
        return (
          <span
            className={priority.urgent ? 'rh-a2-bad' : 'rh-a2-meta'}
            data-testid={`task-row-priority-${task.id}`}
            data-urgent={String(priority.urgent)}
          >
            {priority.text}
          </span>
        );
      },
    },
    {
      key: 'due',
      label: t('webAdmin.tasks.drawer.due'),
      cell: (task) => {
        const kind = dueKind(task.dueAt, now);
        if (kind === 'none') {
          return (
            <span className="rh-a2-meta" data-testid={`task-row-due-${task.id}`}>
              {t('webAdmin.tasks.noDue')}
            </span>
          );
        }
        const days = daysUntilDue(task.dueAt, now);
        return (
          <span
            className={kind === 'overdue' ? 'rh-a2-bad rh-a2-nowrap' : 'rh-a2-nowrap'}
            data-testid={`task-row-due-${task.id}`}
            data-overdue={String(kind === 'overdue')}
          >
            {formatDueDate(task.dueAt)}
            {kind === 'later' && days !== null && days > 1
              ? ` · ${t('webAdmin.tasks.dueInDays', { days: String(days) })}`
              : ''}
          </span>
        );
      },
    },
    {
      key: 'owner',
      label: t('webAdmin.tasks.drawer.owner'),
      cell: (task) => {
        const name = ownerName(task);
        return name === null ? (
          <span className="rh-a2-meta" data-testid={`task-row-no-owner-${task.id}`}>
            {t('webAdmin.tasks.noOwner')}
          </span>
        ) : (
          <span data-testid={`task-row-owner-${task.id}`}>{name}</span>
        );
      },
    },
    {
      key: 'status',
      label: t('webAdmin.tasks.drawer.status'),
      cell: (task) => (
        <span className="rh-a2-meta" data-testid={`task-row-status-${task.id}`}>
          {statusLabel(task)}
        </span>
      ),
    },
    {
      key: 'actions',
      label: t('webAdmin.tasks.columnActions'),
      srOnlyLabel: true,
      align: 'right',
      cell: (task) => {
        const act = action(task);
        return act === null ? null : (
          <button
            type="button"
            className="rh-a2-btn rh-a2-btn--sm"
            data-testid={`task-row-action-${task.id}`}
            onClick={() => act.onAction()}
          >
            {act.label}
          </button>
        );
      },
    },
  ];

  return (
    <ListTable
      columns={columns}
      rows={items}
      rowKey={(task) => task.id}
      rowTestId={(task) => `task-row-${task.id}`}
      testId="task-list"
      caption={t('webAdmin.tasks.boardLabel')}
      {...(empty === undefined ? {} : { empty })}
    />
  );
}
